import {CommentsCategory} from '@domain/comments'
import {Button, DashboardCard, Loading} from '@hm/fundamental'
import {mdiChevronRight} from '@mdi/js'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router'

import {KilnShutdownGanttChart} from './KilnShutdownGanttChart'

import {KilnShutdownForm} from '@refra/modules/common/components/KilnShutdownForm'
import {useFundamentalComments} from '@refra/modules/common/hooks/queries'
import {useRefractoryCustomizationsQuery} from '@refra/modules/common/hooks/queries/customizations'
import type {Shutdown} from '@refra/modules/common/types'
import {useKilnReplacementQuery} from '@refra/modules/refra-planning/hooks/queries'
import {KilnShutdownsRefractoriesProvider} from '@refra/modules/refra-planning/providers'
import type {KilnShutdown} from '@refra/modules/refra-planning/types'
import {useUrlParam} from '@refra/modules/routing/hooks'

interface KilnShutdownCardProps {
  shutdown: Shutdown
}

export const KilnShutdownCard: React.FC<KilnShutdownCardProps> = ({shutdown}) => {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const plantId = useUrlParam('plantCode')
  const {data: kilnReplacement} = useKilnReplacementQuery({
    plantUpmId: shutdown.plantUpmId,
    kilnUpmId: shutdown.kilnUpmId,
    shutdownId: shutdown.id
  })

  const assignedRefractoriesIds = new Set(
    kilnReplacement?.materialInstallations.map((mi) => mi.refractoryId)
  )

  const assignedCommentsIds = new Set(kilnReplacement?.materialInstallations.map((mi) => mi.id))

  const materialInstallationsWithComments =
    useFundamentalComments({
      commentsCategory: CommentsCategory.MaterialInstallations,
      commentCategoryInstanceIds: [...assignedCommentsIds]
    })?.map((comment) => comment.commentsCategoryInstanceId) ?? []

  const isRefractoriesQueryEnabled = assignedRefractoriesIds.size > 0

  const {data: assignedRefractoriesPaginated} = useRefractoryCustomizationsQuery({
    plantUpmId: shutdown.plantUpmId,
    ids: [...assignedRefractoriesIds],
    enabled: isRefractoriesQueryEnabled
  })

  const isKilnShutdownCardLoading =
    !kilnReplacement || (isRefractoriesQueryEnabled && !assignedRefractoriesPaginated)

  if (isKilnShutdownCardLoading) {
    return (
      <DashboardCard className="flex justify-center">
        <Loading size="large" data-cy="kiln_shutdowns_card_loader" />
      </DashboardCard>
    )
  }

  const kilnShutdown: KilnShutdown = {...shutdown, ...kilnReplacement}

  return (
    <KilnShutdownsRefractoriesProvider
      refractories={assignedRefractoriesPaginated?.refractories ?? []}
    >
      <DashboardCard
        className="flex flex-col gap-3 border-gray-200 border-1px"
        data-cy="kiln_shutdown_card"
      >
        <KilnShutdownForm kilnShutdown={kilnShutdown} />
        <KilnShutdownGanttChart
          shutdown={kilnShutdown}
          materialInstallations={kilnShutdown.materialInstallations}
          drillings={kilnShutdown.drillings}
          materialInstallationsWithComments={materialInstallationsWithComments}
          hotSpots={kilnShutdown.hotSpots}
        />
        <div className="flex justify-end flex-row">
          <Button
            variant="tertiary"
            size="small"
            icon={mdiChevronRight}
            onClick={() =>
              navigate(
                `/plants/${plantId}/static-parts/${shutdown.kilnUpmId}?shutdownId=${shutdown.id}`
              )
            }
            iconPosition="right"
          >
            {t('refraPlanning.previewStaticParts')}
          </Button>
        </div>
      </DashboardCard>
    </KilnShutdownsRefractoriesProvider>
  )
}
